import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* Reusable quick-log component for Saved Foods.
   Used on Scan, Calories, and Planner pages.

   Props:
     dayKey?     — "Mon".."Sun". If omitted, logs to today.
     variant?    — "full" (default, with Open + Remove) or "compact" (just + Log)
     onPickResult?(food)   — called when user taps "Open" (only in "full" variant)
     onLogged?(data)       — called after a successful log
     defaultOpen? — when true, panel renders expanded by default
     title?       — override card title ("Saved foods" by default)
     emptyHint?   — override empty-state text
*/

async function fetchSavedFoods(query) {
  const { data } = await api.get(
    `/saved-foods${query ? `?q=${encodeURIComponent(query)}` : ""}`
  );
  return data;
}

export default function SavedFoodsQuickLog({
  dayKey,
  variant = "full",
  onPickResult,
  onLogged,
  defaultOpen = false,
  title,
  emptyHint,
  testidPrefix = "saved-foods",
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState("");
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logging, setLogging] = useState(null);
  const [hasAny, setHasAny] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Initial load + cross-component "saved" event refresh
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchSavedFoods("");
        if (!active) return;
        setFoods(data);
        setHasAny(data.length > 0);
      } catch { /* best-effort */ }
      finally { if (active) setLoading(false); }
    })();
    const refresh = () => setReloadKey((k) => k + 1);
    window.addEventListener("saved-foods:changed", refresh);
    return () => {
      active = false;
      window.removeEventListener("saved-foods:changed", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Debounced search while open
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const id = setTimeout(() => {
      (async () => {
        setLoading(true);
        try {
          const data = await fetchSavedFoods(q);
          if (active) setFoods(data);
        } catch { /* best-effort */ }
        finally { if (active) setLoading(false); }
      })();
    }, 200);
    return () => { active = false; clearTimeout(id); };
  }, [q, open]);

  const triggerReload = () => setReloadKey((k) => k + 1);

  const logFood = async (f) => {
    setLogging(f.food_id);
    try {
      const body = dayKey ? { day_key: dayKey } : {};
      const { data } = await api.post(`/saved-foods/${f.food_id}/log`, body);
      const remaining = data.remaining_kcal;
      const target = data.daily_target_kcal;
      const dayLabel = dayKey || "today";
      const msg = target != null && remaining != null
        ? (remaining < 0
            ? `+${f.kcal} kcal on ${dayLabel} · ${Math.abs(remaining)} over your ${target} target`
            : `+${f.kcal} kcal on ${dayLabel} · ${Math.max(0, remaining)} kcal left ${dayLabel === "today" ? "today" : `for ${dayLabel}`}`)
        : `+${f.kcal} kcal added to ${dayLabel}`;
      toast.success(msg, { duration: 4500 });
      onLogged?.(data);
      triggerReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log");
    } finally {
      setLogging(null);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove "${f.name}" from saved foods?`)) return;
    try {
      await api.delete(`/saved-foods/${f.food_id}`);
      setFoods((prev) => prev.filter((x) => x.food_id !== f.food_id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="ff-card overflow-hidden" data-testid={testidPrefix}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 md:px-6 py-4 hover:bg-[#FAF9F6] transition"
        data-testid={`${testidPrefix}-toggle`}
      >
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-2xl bg-[#FFF6E3] text-[#B07F19] flex items-center justify-center">★</span>
          <div className="text-left">
            <div className="ff-label">{title || "Saved foods"}</div>
            <div className="text-sm font-medium text-[#2A332C]" style={{ fontFamily: "Outfit, sans-serif" }}>
              {hasAny
                ? `Quick-log${dayKey ? ` to ${dayKey}` : ""} from your ${foods.length || ""} saved item${foods.length === 1 ? "" : "s"}`
                : emptyHint || "Build a library of foods you eat often"}
            </div>
          </div>
        </div>
        <span className={`text-[#8B9690] transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>

      {open && (
        <div className="px-5 md:px-6 pb-5 border-t border-[#EAE6DD]">
          <div className="mt-4 relative">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search your saved foods…"
              className="ff-input pr-10"
              data-testid={`${testidPrefix}-search`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B9690]">🔍</span>
          </div>

          {loading ? (
            <div className="text-sm text-[#5C6B61] py-6 text-center">Loading…</div>
          ) : foods.length === 0 ? (
            <div className="text-sm text-[#5C6B61] py-6 text-center" data-testid={`${testidPrefix}-empty`}>
              {q
                ? "No matches. Try a different name."
                : "Scan a food, then hit ☆ Save for later — it'll appear here next time."}
            </div>
          ) : (
            <ul className="mt-3 divide-y divide-[#EAE6DD] max-h-72 overflow-y-auto" data-testid={`${testidPrefix}-list`}>
              {foods.map((f) => (
                <li key={f.food_id} className="flex items-center gap-3 py-3" data-testid={`${testidPrefix}-${f.food_id}`}>
                  {f.image ? (
                    <img src={f.image} alt="" className="w-10 h-10 rounded-xl object-cover flex-none" />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-[#F3F0EA] text-[#8B9690] flex items-center justify-center text-base flex-none">
                      {f.kind === "barcode" ? "📊" : f.kind === "photo" ? "📷" : "🍽️"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{f.name}</div>
                    <div className="text-xs text-[#8B9690]">
                      {f.kcal} kcal{f.portion ? ` · ${f.portion}` : ""}{f.use_count ? ` · used ${f.use_count}×` : ""}
                    </div>
                  </div>
                  {variant === "full" && onPickResult && (
                    <button
                      onClick={() => onPickResult({
                        kind: f.kind || "manual",
                        name: f.name,
                        image: f.image,
                        brand: f.brand,
                        portion: f.portion,
                        estimated_kcal: f.kcal,
                        kcal_per_100g: f.kcal_per_100g,
                        kcal_per_serving: f.kcal_per_serving,
                        macros: f.macros,
                        verdict: "moderate",
                        reason: "From your saved foods library.",
                      })}
                      className="text-xs px-3 py-1.5 rounded-full text-[#3B5A3E] hover:bg-[#E8F0E9] transition"
                      data-testid={`${testidPrefix}-open-${f.food_id}`}
                    >
                      Open
                    </button>
                  )}
                  <button
                    onClick={() => logFood(f)}
                    disabled={logging === f.food_id}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#3B5A3E] text-white hover:bg-[#2c4530] disabled:opacity-60 transition"
                    data-testid={`${testidPrefix}-log-${f.food_id}`}
                  >
                    {logging === f.food_id ? "…" : "+ Log"}
                  </button>
                  {variant === "full" && (
                    <button
                      onClick={() => remove(f)}
                      className="text-[#8B9690] hover:text-[#C36952] text-sm"
                      data-testid={`${testidPrefix}-remove-${f.food_id}`}
                      aria-label={`Remove ${f.name}`}
                    >
                      🗑️
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
